import { useFoldersFilters, useGlobalFoldersFilters } from '@/hooks/useDomains';
import { domainsService } from '@/services/domains.service';
import { useDomainsStore } from '@/store/domains.store';
import { useOrganisationsStore } from '@/store/organisations.store';
import { cn } from '@/utils/cn';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search, User, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Search component for folders
export const FoldersSearchBar = () => {
  const { t } = useTranslation();
  const { foldersSearchQuery, setFoldersSearchQuery } = useDomainsStore();
  const [localSearch, setLocalSearch] = useState(foldersSearchQuery);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFoldersSearchQuery(localSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch, setFoldersSearchQuery]);

  return (
    <div className="relative min-w-0 flex-1">
      <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
      <input
        type="text"
        placeholder={t(
          'domains.folders.search.placeholder',
          'Search folders...'
        )}
        value={localSearch}
        onChange={e => setLocalSearch(e.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 py-2.5 pr-4 pl-10 text-sm transition-colors focus:ring-2"
      />
    </div>
  );
};

// Responsable filter for folders (domain-scoped)
export const FoldersResponsableSelect = ({
  options,
}: {
  options: { name: string; email?: string }[];
}) => {
  const { t } = useTranslation();
  const {
    foldersResponsableFilter,
    setFoldersResponsableFilter,
    setFoldersCurrentPage,
  } = useDomainsStore();

  return (
    <div className="relative min-w-[200px]">
      <select
        value={foldersResponsableFilter}
        onChange={e => {
          setFoldersResponsableFilter(e.target.value);
          setFoldersCurrentPage(1);
        }}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2.5 pr-8 text-sm transition-colors focus:ring-2 sm:w-auto"
      >
        <option value="">
          {t('domains.folders.filter.responsable.all', 'All Managers')}
        </option>
        {options.map((r, idx) => (
          <option
            key={`${r.name}-${r.email ?? idx}`}
            value={r.name}
            title={r.email || undefined}
          >
            {r.name}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Status filter for folders
export const FoldersStatusFilter = () => {
  const { t } = useTranslation();
  const { foldersStatusFilter, handleStatusFilter } = useFoldersFilters();

  return (
    <div className="relative min-w-0 flex-shrink-0">
      <select
        value={foldersStatusFilter}
        onChange={e =>
          handleStatusFilter(e.target.value as typeof foldersStatusFilter)
        }
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2.5 pr-8 text-sm transition-colors focus:ring-2 sm:w-auto"
      >
        <option value="all">
          {t('domains.folders.filter.all', 'All Status')}
        </option>
        <option value="active">
          {t('domains.folders.filter.active', 'Active')}
        </option>
        <option value="inactive">
          {t('domains.folders.filter.inactive', 'Inactive')}
        </option>
        <option value="archived">
          {t('domains.folders.filter.archived', 'Archivé')}
        </option>
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Sort dropdown for folders
export const FoldersSortDropdown = () => {
  const { t } = useTranslation();
  const { foldersSortBy, foldersSortOrder, handleSort } = useFoldersFilters();

  const handleSortChange = (value: string) => {
    const [field, order] = value.split('-') as [
      'name' | 'totalDocuments' | 'updatedAt' | 'createdAt',
      'ASC' | 'DESC',
    ];
    handleSort(field, order);
  };

  return (
    <div className="relative min-w-0 flex-shrink-0">
      <select
        value={`${foldersSortBy}-${foldersSortOrder}`}
        onChange={e => handleSortChange(e.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2.5 pr-8 text-sm transition-colors focus:ring-2 sm:w-auto"
      >
        <option value="name-ASC">
          {t('domains.folders.sort.nameAsc', 'Name A-Z')}
        </option>
        <option value="name-DESC">
          {t('domains.folders.sort.nameDesc', 'Name Z-A')}
        </option>
        <option value="totalDocuments-DESC">
          {t('domains.folders.sort.documentsDesc', 'Most Documents')}
        </option>
        <option value="totalDocuments-ASC">
          {t('domains.folders.sort.documentsAsc', 'Least Documents')}
        </option>
        <option value="updatedAt-DESC">
          {t('domains.folders.sort.recentlyUpdated', 'Recently Updated')}
        </option>
        <option value="createdAt-DESC">
          {t('domains.folders.sort.newest', 'Newest First')}
        </option>
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Page size selector for domain folders
export const FoldersPageSizeSelector = () => {
  const { t } = useTranslation();
  const { foldersLimit, setFoldersLimit } = useDomainsStore();

  const pageSizeOptions = [10, 20, 50, 100];

  return (
    <div className="relative min-w-0 flex-shrink-0">
      <select
        value={foldersLimit}
        onChange={e => setFoldersLimit(parseInt(e.target.value))}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2.5 pr-8 text-sm transition-colors focus:ring-2 sm:w-auto"
      >
        {pageSizeOptions.map(size => (
          <option key={size} value={size}>
            {t('domains.folders.pageSize.option', '{{count}} per page', {
              count: size,
            })}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Global Search component for folders
export const GlobalFoldersSearchBar = () => {
  const { t } = useTranslation();
  const { globalFoldersSearchQuery, setGlobalFoldersSearchQuery } =
    useDomainsStore();
  const [localSearch, setLocalSearch] = useState(globalFoldersSearchQuery);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFoldersSearchQuery(localSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch, setGlobalFoldersSearchQuery]);

  return (
    <div className="relative min-w-0 flex-1">
      <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
      <input
        type="text"
        placeholder={t(
          'domains.folders.search.placeholder',
          'Search folders...'
        )}
        value={localSearch}
        onChange={e => setLocalSearch(e.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 py-2.5 pr-4 pl-10 text-sm transition-colors focus:ring-2"
      />
    </div>
  );
};

// Responsable filter for global folders (cross-domain)
export const GlobalFoldersResponsableSelect = () => {
  const { t } = useTranslation();
  const {
    globalFoldersResponsableFilter,
    setGlobalFoldersResponsableFilter,
    setGlobalFoldersCurrentPage,
  } = useDomainsStore();

  const { currentOrganisation } = useOrganisationsStore();
  const organisationUuid = currentOrganisation?.uuid;

  const { data: responsables = [], isLoading } = useQuery({
    queryKey: ['domains', 'responsables', organisationUuid],
    queryFn: () => domainsService.getResponsables(),
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="relative min-w-[200px]">
      <select
        value={globalFoldersResponsableFilter}
        onChange={e => {
          setGlobalFoldersResponsableFilter(e.target.value);
          setGlobalFoldersCurrentPage(1);
        }}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2.5 pr-8 text-sm transition-colors focus:ring-2 sm:w-auto"
        disabled={isLoading}
      >
        <option value="">
          {t('domains.folders.filter.responsable.all', 'All Managers')}
        </option>
        {responsables.map((r, idx) => (
          <option
            key={`${r.name}-${r.email ?? idx}`}
            value={r.name}
            title={r.email || undefined}
          >
            {r.name}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Toggle « Mes dossiers » / « Tous les dossiers » (cloisonnement volontaire).
// Ne s'affiche que s'il y a une organisation courante (sinon sans objet).
export const GlobalFoldersScopeToggle = () => {
  const { t } = useTranslation();
  const { globalFoldersScope, setGlobalFoldersScope } = useDomainsStore();
  const { currentOrganisation } = useOrganisationsStore();

  if (!currentOrganisation) return null;

  const options = [
    {
      value: 'mine' as const,
      label: t('domains.folders.scope.mine', 'Mes dossiers'),
      icon: User,
    },
    {
      value: 'all' as const,
      label: t('domains.folders.scope.all', 'Tous les dossiers'),
      icon: Users,
    },
  ];

  return (
    <div
      role="group"
      aria-label={t('domains.folders.scope.label', 'Périmètre des dossiers')}
      className="inline-flex flex-shrink-0 items-center rounded-lg border border-neutral-300 bg-white p-0.5"
    >
      {options.map(option => {
        const Icon = option.icon;
        const active = globalFoldersScope === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => setGlobalFoldersScope(option.value)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              active
                ? 'bg-primary-600 text-white'
                : 'text-neutral-600 hover:bg-neutral-100'
            )}
          >
            <Icon className="h-4 w-4" />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// Global Status filter component for folders
export const GlobalFoldersStatusFilter = () => {
  const { t } = useTranslation();
  const { handleStatusFilter, globalFoldersStatusFilter } =
    useGlobalFoldersFilters();
  const [isOpen, setIsOpen] = useState(false);

  const statusOptions = [
    { value: 'all', label: t('domains.filters.status.all', 'All Status') },
    { value: 'active', label: t('domains.folders.active', 'Active') },
    { value: 'inactive', label: t('domains.folders.inactive', 'Inactive') },
    {
      value: 'archived',
      label: t('domains.folders.filter.archived', 'Archivé'),
    },
  ] as const;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-w-[140px] items-center justify-between gap-2 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm transition-colors hover:border-neutral-400"
      >
        <span className="text-neutral-700">
          {
            statusOptions.find(opt => opt.value === globalFoldersStatusFilter)
              ?.label
          }
        </span>
        <ChevronDown
          className={`h-4 w-4 text-neutral-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="absolute top-full z-10 mt-1 w-full rounded-lg border border-neutral-200 bg-white shadow-lg">
          {statusOptions.map(option => (
            <button
              key={option.value}
              onClick={() => {
                handleStatusFilter(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-neutral-50 ${
                globalFoldersStatusFilter === option.value
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-neutral-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Page size selector for global folders
export const GlobalFoldersPageSizeSelector = () => {
  const { t } = useTranslation();
  const { globalFoldersLimit, setGlobalFoldersLimit } = useDomainsStore();

  const pageSizeOptions = [10, 20, 50, 100];

  return (
    <div className="relative min-w-0 flex-shrink-0">
      <select
        value={globalFoldersLimit}
        onChange={e => setGlobalFoldersLimit(parseInt(e.target.value))}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2.5 pr-8 text-sm transition-colors focus:ring-2 sm:w-auto"
      >
        {pageSizeOptions.map(size => (
          <option key={size} value={size}>
            {t('domains.folders.pageSize.option', '{{count}} per page', {
              count: size,
            })}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Global Sync Status filter component for folders
export const GlobalFoldersSyncStatusFilter = () => {
  const { t } = useTranslation();
  const { handleSyncStatusFilter, globalFoldersSyncStatusFilter } =
    useGlobalFoldersFilters();
  const [isOpen, setIsOpen] = useState(false);

  const syncStatusOptions = [
    {
      value: 'all',
      label: t('domains.filters.syncStatus.all', 'All Sync Status'),
    },
    { value: 'synced', label: t('domains.folders.synced', 'Synced') },
    {
      value: 'not_synced',
      label: t('domains.folders.notSynced', 'Not Synced'),
    },
  ] as const;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-w-[160px] items-center justify-between gap-2 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm transition-colors hover:border-neutral-400"
      >
        <span className="text-neutral-700">
          {
            syncStatusOptions.find(
              opt => opt.value === globalFoldersSyncStatusFilter
            )?.label
          }
        </span>
        <ChevronDown
          className={`h-4 w-4 text-neutral-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="absolute top-full z-10 mt-1 w-full rounded-lg border border-neutral-200 bg-white shadow-lg">
          {syncStatusOptions.map(option => (
            <button
              key={option.value}
              onClick={() => {
                handleSyncStatusFilter(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-neutral-50 ${
                globalFoldersSyncStatusFilter === option.value
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-neutral-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Global Domain Name filter component for folders
export const GlobalFoldersDomainNameFilter = () => {
  const { t } = useTranslation();
  const { globalFoldersDomainNameFilter, setGlobalFoldersDomainNameFilter } =
    useDomainsStore();
  const [localDomainName, setLocalDomainName] = useState(
    globalFoldersDomainNameFilter
  );

  // Debounce domain name filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFoldersDomainNameFilter(localDomainName);
    }, 500);

    return () => clearTimeout(timer);
  }, [localDomainName, setGlobalFoldersDomainNameFilter]);

  return (
    <div className="relative min-w-[160px]">
      <input
        type="text"
        placeholder={t(
          'domains.folders.domainFilter.placeholder',
          'Filter by domain...'
        )}
        value={localDomainName}
        onChange={e => setLocalDomainName(e.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm transition-colors focus:ring-2"
      />
    </div>
  );
};

// Global Sort dropdown component for folders
export const GlobalFoldersSortDropdown = () => {
  const { t } = useTranslation();
  const { handleSort, globalFoldersSortBy, globalFoldersSortOrder } =
    useGlobalFoldersFilters();
  const [isOpen, setIsOpen] = useState(false);

  const sortOptions = [
    {
      field: 'name',
      order: 'ASC',
      label: t('domains.sort.nameAsc', 'Name A-Z'),
    },
    {
      field: 'name',
      order: 'DESC',
      label: t('domains.sort.nameDesc', 'Name Z-A'),
    },
    {
      field: 'createdAt',
      order: 'DESC',
      label: t('domains.sort.newest', 'Newest First'),
    },
    {
      field: 'createdAt',
      order: 'ASC',
      label: t('domains.sort.oldest', 'Oldest First'),
    },
    {
      field: 'updatedAt',
      order: 'DESC',
      label: t('domains.sort.recentlyUpdated', 'Recently Updated'),
    },
    {
      field: 'totalDocuments',
      order: 'DESC',
      label: t('domains.sort.mostDocuments', 'Most Documents'),
    },
  ] as const;

  const currentSort = sortOptions.find(
    opt =>
      opt.field === globalFoldersSortBy && opt.order === globalFoldersSortOrder
  );

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-w-[160px] items-center justify-between gap-2 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm transition-colors hover:border-neutral-400"
      >
        <span className="text-neutral-700">
          {currentSort?.label || t('domains.sort.default', 'Sort')}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-neutral-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="absolute top-full z-10 mt-1 w-full rounded-lg border border-neutral-200 bg-white shadow-lg">
          {sortOptions.map(option => (
            <button
              key={`${option.field}-${option.order}`}
              onClick={() => {
                handleSort(option.field, option.order);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-neutral-50 ${
                globalFoldersSortBy === option.field &&
                globalFoldersSortOrder === option.order
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-neutral-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Global Folders Pagination component
export const GlobalFoldersPagination = () => {
  const { t } = useTranslation();
  const {
    globalFoldersCurrentPage,
    globalFoldersTotalPages,
    globalFoldersTotalItems,
    globalFoldersHasNext,
    globalFoldersHasPrev,
    globalFoldersLimit,
    setGlobalFoldersCurrentPage,
  } = useDomainsStore();

  if (globalFoldersTotalPages <= 1) return null;

  const goToPage = (page: number) => {
    if (page >= 1 && page <= globalFoldersTotalPages) {
      setGlobalFoldersCurrentPage(page);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      1,
      globalFoldersCurrentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(
      globalFoldersTotalPages,
      startPage + maxVisiblePages - 1
    );

    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <button
          key={page}
          onClick={() => goToPage(page)}
          className={`rounded-lg px-3 py-2 text-sm transition-colors ${
            page === globalFoldersCurrentPage
              ? 'bg-primary-600 text-white'
              : 'text-neutral-700 hover:bg-neutral-100'
          }`}
        >
          {page}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-neutral-600">
        {t('pagination.showing', 'Showing')}{' '}
        {(globalFoldersCurrentPage - 1) * globalFoldersLimit + 1} -{' '}
        {Math.min(
          globalFoldersCurrentPage * globalFoldersLimit,
          globalFoldersTotalItems
        )}{' '}
        {t('pagination.of', 'of')} {globalFoldersTotalItems}{' '}
        {t('domains.folders.results', 'folders')}
      </p>

      <div className="flex items-center gap-2">
        <button
          onClick={() => goToPage(globalFoldersCurrentPage - 1)}
          disabled={!globalFoldersHasPrev}
          className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('pagination.previous', 'Previous')}
        </button>

        <div className="hidden items-center gap-1 sm:flex">
          {renderPageNumbers()}
        </div>

        <button
          onClick={() => goToPage(globalFoldersCurrentPage + 1)}
          disabled={!globalFoldersHasNext}
          className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('pagination.next', 'Next')}
        </button>
      </div>
    </div>
  );
};
